import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { Divider as HotsauceDivider, Stack, Text, tokens } from '@hotjar/hotsauce';
import { UnitRate } from './UnitRate';
import { Coverage } from './Coverage';
import { MissingFeaturesList } from './MissingFeaturesList';
import { Product } from './Product';
import { CurrentPlanCard } from './CurrentPlanCard';
import { NewPlanCard } from './NewPlanCard';
import type { ChurnDeflectionObserveProduct } from '../types';
import { calculateCoverage } from '../utils/calculateCoverage';
import { composePlanName } from '../utils/composePlanName';

const Wrapper = styled(Product)`
  background: radial-gradient(50% 75% at 10% 80%, rgb(255 199 123) 0%, rgb(252 250 229) 100%)
    rgb(253, 241, 222);
`;

const Divider = styled(HotsauceDivider)`
  margin: 0;
  border-color: ${tokens.color.neutral_300};
`;

type Props = ChurnDeflectionObserveProduct & { dailySessions: number; subscriptionEndDate: string };

export function ObserveProduct({
  currentPlan,
  newPlan,
  dailySessions,
  subscriptionEndDate,
}: Props): JSX.Element {
  const { t } = useTranslation('voluntary-churn');

  const { name: currentPlanName, dailyAllowance: currentPlanDailyAllowance } = currentPlan;
  const { name: newPlanName, dailyAllowance: newPlanDailyAllowance } = newPlan;

  const currentCoverage = calculateCoverage(dailySessions, currentPlanDailyAllowance);
  const newCoverage = calculateCoverage(dailySessions, newPlanDailyAllowance);
  const lossRate = currentCoverage - newCoverage;

  return (
    <Wrapper>
      <Text as="h2" textType="header4">
        Observe
      </Text>
      <Stack customSpacing={tokens.spacer.x3}>
        <CurrentPlanCard
          title={composePlanName(currentPlanName, currentPlanDailyAllowance)}
          date={t('churn.deflection-modal.now-date')}
          testId="observe-current-plan-title"
        >
          <UnitRate
            title={t('churn.deflection-modal.approximated-sessions-label')}
            numerator={dailySessions}
            denominator={t('churn.deflection-modal.day')}
            testId="observe-current-plan-daily-sessions"
          />
          <Divider spacing="xs" />
          <Coverage
            title={t('churn.deflection-modal.coverage', { coverage: currentCoverage })}
            subtitle={t('churn.deflection-modal.current-tracking-coverage', {
              coverage: currentCoverage,
            })}
            coverage={currentCoverage}
          />
        </CurrentPlanCard>
        <NewPlanCard title={newPlanName} date={subscriptionEndDate} testId="observe-new-plan-title">
          <Stack customSpacing={tokens.spacer.x3}>
            <Stack direction="column" customSpacing={tokens.spacer.x2} css={{ flex: 6 }}>
              <UnitRate
                title={t('churn.deflection-modal.maximum-sessions-label')}
                numerator={newPlanDailyAllowance}
                denominator={t('churn.deflection-modal.day')}
                testId="observe-new-plan-daily-sessions"
              />
              <Divider spacing="xs" />
              <Coverage
                title={t('churn.deflection-modal.coverage', { coverage: newCoverage })}
                subtitle={t('churn.deflection-modal.basic-plan-tracking-coverage', {
                  coverage: newCoverage,
                })}
                coverage={newCoverage}
                lossRate={lossRate}
              />
            </Stack>
            <Stack css={{ flex: 6 }}>
              <MissingFeaturesList
                title={t('churn.deflection-modal.missing-features-list-label')}
                testId="observe-missing-features-list"
                features={
                  currentPlanName === 'business'
                    ? ['user-attributes', 'advanced-filtering', 'third-party-integrations']
                    : ['advanced-filtering', 'recordings-by-page-or-event']
                }
              />
            </Stack>
          </Stack>
        </NewPlanCard>
      </Stack>
    </Wrapper>
  );
}
